/**
 * Auth API — thin typed wrappers over the backend's existing OTP endpoints.
 *
 * No backend change is involved: these are the same endpoints BigxBookApp and
 * BigxBookDesktop already use. `verify-otp` returns the merchant's store list,
 * which is what makes the web store switcher possible without new server work.
 */
import { http } from '@/services/httpClient';
import { tokenStore } from '@/services/tokenStore';
import type { UserInfo } from '@/store/useAppStore';

export interface StoreSummary {
  id: string;
  name: string;
}

export interface VerifyOtpResponse {
  access_token: string;
  refresh_token: string;
  user: UserInfo;
  /** `[{ id, name }]` — empty for first-time users who have no store yet. */
  stores: StoreSummary[];
}

/** Sends an OTP to the given phone. Anonymous — no session exists yet. */
export async function requestOtp(phone: string): Promise<{ message: string }> {
  return http<{ message: string }>('/auth/request-otp', {
    method: 'POST',
    anonymous: true,
    body: { phone },
  });
}

/**
 * Exchanges an OTP for a token pair, the user record and the store list.
 * Tokens are handed straight to `tokenStore`, which is the only module that
 * decides how they are held.
 */
export async function verifyOtp(phone: string, otp: string): Promise<VerifyOtpResponse> {
  const res = await http<VerifyOtpResponse>('/auth/verify-otp', {
    method: 'POST',
    anonymous: true,
    body: { phone, otp },
  });
  tokenStore.setTokens(res.access_token, res.refresh_token);
  return res;
}

/**
 * Registers a number with a PIN. Deliberately returns NO tokens: the account
 * is created PENDING and stays unusable until an admin approves it.
 */
export async function registerWithPin(
  phone: string,
  pin: string,
): Promise<{ status: string; message: string }> {
  return http<{ status: string; message: string }>('/auth/register-pin', {
    method: 'POST',
    anonymous: true,
    body: { phone, pin },
  });
}

/**
 * Exchanges a phone + PIN for the same payload `verifyOtp` returns, so every
 * caller downstream — the store switcher, the onboarding gate — is unchanged.
 *
 * Errors are left to the caller rather than being flattened here: the server
 * distinguishes a wrong PIN (which counts towards the lockout) from an account
 * awaiting approval (which does not), and the login screen needs to say
 * different things about each.
 */
export async function loginWithPin(phone: string, pin: string): Promise<VerifyOtpResponse> {
  const res = await http<VerifyOtpResponse>('/auth/login-pin', {
    method: 'POST',
    anonymous: true,
    body: { phone, pin },
  });
  tokenStore.setTokens(res.access_token, res.refresh_token);
  return res;
}

/**
 * Changes the signed-in user's PIN.
 *
 * The PIN is a server-side credential: it is bcrypt-hashed there and stripped
 * from sync in both directions, so it cannot be set by writing to the local
 * database. The Settings screen used to do exactly that, which quietly stopped
 * working the moment the PIN became something you log in with.
 */
export async function changePin(
  currentPin: string | undefined,
  newPin: string,
): Promise<{ ok: boolean; message: string }> {
  return http<{ ok: boolean; message: string }>('/auth/change-pin', {
    method: 'POST',
    body: { currentPin, newPin },
  });
}
